
import { Control } from "react-hook-form";
import { FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const blockchains = [
  { value: "polygon", name: "Polygon" },
];

interface BlockchainConfigProps {
  control: Control<any>;
  watchMultichain: boolean;
}

export const BlockchainConfig = ({ control, watchMultichain }: BlockchainConfigProps) => {
  return (
    <div className="space-y-4">
      <h3 className="text-lg font-medium">Blockchain Configuration</h3>
      
      <FormField
        control={control}
        name="chain"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Primary Blockchain</FormLabel>
            <Select 
              onValueChange={field.onChange} 
              defaultValue={field.value}
            >
              <FormControl>
                <SelectTrigger>
                  <SelectValue placeholder="Select blockchain" />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                {blockchains.map((blockchain) => (
                  <SelectItem key={blockchain.value} value={blockchain.value}>
                    {blockchain.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <FormDescription>
              The primary blockchain where your DAO will be deployed
            </FormDescription>
            <FormMessage />
          </FormItem>
        )}
      />
      
      <FormField
        control={control}
        name="multichain"
        render={({ field }) => (
          <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
            <FormControl>
              <Checkbox
                checked={field.value}
                onCheckedChange={field.onChange}
              />
            </FormControl>
            <div className="space-y-1 leading-none">
              <FormLabel>
                Enable Multi-chain Deployment
              </FormLabel>
              <FormDescription>
                Deploy the same DAO with identical contract addresses across multiple blockchains
              </FormDescription>
            </div>
          </FormItem>
        )}
      />

      {watchMultichain && (
        <FormField
          control={control}
          name="additionalChains"
          render={() => (
            <FormItem>
              <FormLabel>Additional Blockchains</FormLabel>
              <FormDescription className="mb-3">
                Select additional blockchains for multi-chain deployment
              </FormDescription>
              <div className="grid grid-cols-2 gap-3">
                {blockchains.map((blockchain) => (
                  <FormField
                    key={blockchain.value}
                    control={control}
                    name="additionalChains"
                    render={({ field }) => {
                      return (
                        <FormItem
                          key={blockchain.value}
                          className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-3"
                        >
                          <FormControl>
                            <Checkbox
                              checked={field.value?.includes(blockchain.value)}
                              onCheckedChange={(checked) => {
                                return checked
                                  ? field.onChange([...(field.value || []), blockchain.value])
                                  : field.onChange(
                                      field.value?.filter(
                                        (value: string) => value !== blockchain.value
                                      )
                                    );
                              }}
                            />
                          </FormControl>
                          <FormLabel className="font-normal text-sm">
                            {blockchain.name}
                          </FormLabel>
                        </FormItem>
                      );
                    }}
                  />
                ))}
              </div>
              <FormMessage />
            </FormItem>
          )}
        />
      )}
      
      <FormField
        control={control}
        name="acceptedTokens"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Accepted Tokens</FormLabel>
            <FormControl>
              <Input 
                placeholder="USDC,MATIC,DAI" 
                {...field} 
              />
            </FormControl>
            <FormDescription>
              Comma-separated list of tokens that can be used for contributions
            </FormDescription>
            <FormMessage />
          </FormItem>
        )}
      />
    </div>
  );
};
